import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import type { RootState } from '../store';
import { logoutUser as logoutAction, updateUser, clearAuth } from '../store/slices/authSlice';
import type { UserResponse } from '../types';
import { useAppDispatch } from '../store/hooks'

// ../store/hooks'; // If you have this hook, use it

/**
 * Custom hook for authentication state and operations.
 * Provides access to authentication state from Redux and basic operations.
 */
export const useAuth = () => {
  // Use AppDispatch instead of the default dispatch type
  const dispatch = useAppDispatch(); // OR: const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  
  // Select authentication state from Redux store
  const { user, token, role, isAuthenticated, isLoading } = useSelector((state: RootState) => state.auth);

  /**
   * Logout operation
   * Clears authentication data and redirects to landing page.
   */
  const logout = useCallback(async () => {
    try {
      // Dispatch the async thunk and unwrap the result to handle rejections
      await dispatch(logoutAction()).unwrap();
      // If successful, the thunk's fulfilled case will have already cleared the state.
      navigate('/');
    } catch (error) {
      // The 'error' caught here is of type 'unknown'.
      // We must perform a type guard to safely use it.
      let errorMessage = 'An unknown error occurred during logout.';
      if (typeof error === 'string') {
        errorMessage = error;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      console.error('Logout failed:', errorMessage);
      
      // Regardless of the backend error, ensure the user is logged out locally
      dispatch(clearAuth());
      navigate('/');
    }
  }, [dispatch, navigate]);

  /**
   * Update user profile
   * Updates user information in Redux store
   */
  const updateUserProfile = useCallback((userData: Partial<UserResponse>) => {
    dispatch(updateUser(userData));
  }, [dispatch]);

  /**
   * Role checking utilities
   */
  const isAthlete = useCallback((): boolean => {
    return role === 'athlete' || role === 'both';
  }, [role]);

  const isTrainer = useCallback((): boolean => {
    return role === 'trainer' || role === 'both';
  }, [role]);

  const isSuperuser = useCallback((): boolean => {
    return role === 'superuser';
  }, [role]);

  const hasRole = useCallback((requiredRole: string | string[]): boolean => {
    if (!role) return false;
    
    const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    
    if (role === 'superuser') return true;
    if (roles.includes(role)) return true;
    
    if (role === 'both' && (roles.includes('athlete') || roles.includes('trainer'))) {
      return true;
    }
    
    return false;
  }, [role]);

  return {
    // State
    user,
    token,
    role,
    isAuthenticated,
    isLoading,
    
    // Operations
    logout,
    updateUserProfile,
    
    // Role utilities
    isAthlete,
    isTrainer,
    isSuperuser,
    hasRole,
  };
};
